import { __assign, __rest } from "tslib";
import { EditorState, Modifier, SelectionState, CharacterMetadata, } from 'wix-rich-content-editor-common';
import { createBasePlugin } from 'wix-rich-content-plugin-commons';
import decorateComponentWithProps from 'decorate-component-with-props';
import createToolbar from './toolbar';
import { DA_EMOTE_TYPE } from './types';
import emoteStrategy from './strategy';
import attachImmutableEntitiesToEmotes from './attach';
import Component from './emote-component';
import ddt from '@wix/da-ddt';
var logger = ddt.createLogger('editor.emote');
/**
 * Having an immutable at the end of a block breaks many things
 * this function adds a space after the emote if its the last thing
 * in the block.
 *
 * @param newEditorState
 */
function fixLastImmutable(newEditorState) {
    var contentState = newEditorState.getCurrentContent();
    var blockMap = contentState.getBlockMap();
    blockMap.map(function (block) {
        var lastChar = block.getCharacterList().last();
        if (lastChar && lastChar.getEntity()) {
            var lastEntity = contentState.getEntity(lastChar.getEntity());
            if (lastEntity &&
                lastEntity.getMutability() === 'IMMUTABLE' &&
                (lastEntity.getType() === 'DA_OFF_EMOTE' ||
                    lastEntity.getType() === 'DA_COMM_EMOTE')) {
                var textLength = block.getText().length;
                var selection = SelectionState.createEmpty(block.getKey());
                var newSel = selection.merge({
                    focusOffset: textLength,
                    anchorOffset: textLength,
                });
                logger.log('Fixing block', block.getKey(), newSel.serialize());
                var contentStateWithInsertedText = Modifier.insertText(contentState, newSel, ' ');
                newEditorState = EditorState.push(newEditorState, contentStateWithInsertedText, 'insert-last-space');
            }
        }
        return block;
    });
    return newEditorState;
}
/**
 * Because of the immutable fix deleting emotes is a problem because
 * backspace just adds another space and you end up back where you started.
 * This function is called after the last backspace and removes the emote
 * where the editor caret is (end of the current block).
 *
 * @param newEditorState
 */
function handleBackspace(newEditorState) {
    var contentState = newEditorState.getCurrentContent();
    var selection = newEditorState.getSelection();
    // We are only interested in backspace which should never be expanded
    // expanded = multiple chars selected
    if (!selection.isCollapsed()) {
        return newEditorState;
    }
    logger.log('Backspace selection', selection.serialize());
    var selectionBlock = contentState.getBlockForKey(selection.getAnchorKey());
    var selectionText = selectionBlock.getText();
    // Bail if we are not at the end of the block
    if (selection.getStartOffset() !== selectionText.length) {
        logger.log('Backspace selection not at end, bailed');
        return newEditorState;
    }
    // Now check if the last char IS an emote
    var lastChar = selectionBlock.getCharacterList().last();
    if (lastChar && lastChar.getEntity()) {
        logger.log('Backspace will remove emote', lastChar);
        var lastEntity_1 = contentState.getEntity(lastChar.getEntity());
        if (lastEntity_1 &&
            lastEntity_1.getMutability() === 'IMMUTABLE' &&
            (lastEntity_1.getType() === 'DA_OFF_EMOTE' ||
                lastEntity_1.getType() === 'DA_COMM_EMOTE')) {
            var entityData_1 = (lastEntity_1.getData() || {}).emote;
            logger.log('Backspace removing emote: ', lastEntity_1.getData());
            // Now we remove the emote from the text, this means
            // - find the entity within the blocks
            // - remove the entity data from the char
            // - remove the text from the block
            // - remove the chars that were removed from the char list
            // - merge it all back into the blocks/state
            var blockMap = contentState.getBlockMap();
            var blocks = blockMap.map(function (block) {
                var chars = block.getCharacterList().map(function (char) {
                    if (char.getEntity() === lastChar.getEntity()) {
                        return CharacterMetadata.applyEntity(char, null);
                    }
                    return char;
                });
                if (lastEntity_1.getType() === 'DA_COMM_EMOTE') {
                    var text = block.getText();
                    var newText = text.substring(0, text.length - 1);
                    if (newText === text) {
                        return block; // Nope...
                    }
                    // Remove the removed chars
                    block = block.set('characterList', chars.slice(0, text.length - entityData_1.length));
                    // Set the new text
                    block = block.set('text', newText);
                }
                else {
                    var text = block.getText();
                    var emoteRegex = new RegExp(entityData_1 + "$"); // match emote at end
                    var newText = text.replace(emoteRegex, '');
                    if (newText === text) {
                        return block; // Nope...
                    }
                    // Remove the removed chars
                    block = block.set('characterList', chars.slice(0, text.length - entityData_1.length));
                    // Set the new text
                    block = block.set('text', newText);
                }
                return block;
            });
            var newContentState = contentState.merge({
                blockMap: blockMap.merge(blocks),
            });
            newEditorState = EditorState.set(newEditorState, {
                currentContent: newContentState,
            });
        }
    }
    return newEditorState;
}
function emotePluginCreator() {
    return function (config) {
        var type = DA_EMOTE_TYPE;
        var _a = config, decorator = _a.decorator, helpers = _a.helpers, theme = _a.theme, isMobile = _a.isMobile, t = _a.t, _b = type, settings = _a[_b], getEditorState = _a.getEditorState, setEditorState = _a.setEditorState, rest = __rest(_a, ["decorator", "helpers", "theme", "isMobile", "t", typeof _b === "symbol" ? _b : _b + "", "getEditorState", "setEditorState"]); // TODO Remove any when upstream supports custom plugins;
        var wasDelete = false;
        var wasSplit = false;
        return createBasePlugin(__assign({ decorator: decorator,
            theme: theme, type: type, toolbar: createToolbar({
                helpers: helpers,
                t: t,
                settings: settings,
                getEditorState: getEditorState,
                setEditorState: setEditorState,
            }), helpers: helpers,
            isMobile: isMobile,
            t: t }, rest), {
            decorators: [
                {
                    strategy: emoteStrategy,
                    component: decorateComponentWithProps(Component, { theme: theme }),
                },
            ],
            onChange: function (editorState) {
                if (wasSplit) {
                    wasSplit = false;
                    return editorState;
                }
                var newEditorState = attachImmutableEntitiesToEmotes(editorState);
                if (wasDelete) {
                    wasDelete = false;
                    newEditorState = handleBackspace(newEditorState);
                }
                newEditorState = fixLastImmutable(newEditorState);
                return newEditorState;
            },
            handleKeyCommand: function (command) {
                if (command === 'backspace') {
                    wasDelete = true;
                }
                if (command === 'split-block') {
                    wasSplit = true;
                }
                return 'not-handled';
            },
        });
    };
}
export { emotePluginCreator, DA_EMOTE_TYPE };
//# sourceMappingURL=createEmotePlugin.js.map